import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { getSubdomainRoute, parseSubdomainURL } from '../services/subdomainRoutingService';
import { getStaticHTMLContent } from '../services/staticHostingService';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../services/firebase';
import LoadingSpinner from './LoadingSpinner';

// HTMLコンテンツからhead要素を解析する関数
const parseHeadContent = (htmlContent) => {
    if (!htmlContent) return [];

    const parser = new DOMParser();
    const doc = parser.parseFromString(`<div>${htmlContent}</div>`, 'text/html');
    const elements = [];

    // div内のすべての子要素を処理
    const container = doc.querySelector('div');
    if (container) {
        Array.from(container.children).forEach((element, index) => {
            const tagName = element.tagName.toLowerCase();
            const props = {};

            // 属性を取得
            Array.from(element.attributes).forEach(attr => {
                props[attr.name] = attr.value;
            });

            // 内容を取得
            if (element.textContent && element.textContent.trim()) {
                props.children = element.textContent;
            }

            // Helmetがサポートする要素のみ追加
            if (['meta', 'link', 'script', 'style', 'title', 'base', 'noscript'].includes(tagName)) {
                elements.push({
                    type: tagName,
                    props: props,
                    key: `custom-head-${index}`
                });
            }
        });
    }

    return elements;
};

// 完全なHTMLにグローバル設定を挿入する関数
const insertGlobalSettingsIntoHTML = (htmlContent, settings) => {
    let modifiedHTML = htmlContent;

    // カスタムheadの挿入
    if (settings.customHead && settings.customHead.trim()) {
        const headInsertionPoint = '</head>';
        const customHeadWithComment = `<!-- Global Settings Custom Head -->\n${settings.customHead}\n${headInsertionPoint}`;
        modifiedHTML = modifiedHTML.replace(headInsertionPoint, customHeadWithComment);
    }

    // カスタムbodyの挿入
    if (settings.customBody && settings.customBody.trim()) {
        const bodyOpenTag = '<body>';
        const bodyInsertionPoint = bodyOpenTag;
        const customBodyWithComment = `${bodyOpenTag}\n<!-- Global Settings Custom Body -->\n${settings.customBody}\n`;
        modifiedHTML = modifiedHTML.replace(bodyInsertionPoint, customBodyWithComment);
    }

    return modifiedHTML;
};

const SubdomainRouter = () => {
    const [routeInfo, setRouteInfo] = useState(null);
    const [htmlContent, setHtmlContent] = useState('');
    const [pageData, setPageData] = useState(null);
    const [globalSettings, setGlobalSettings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // グローバル設定を取得する関数
    const fetchGlobalSettings = async (accountId) => {
        try {
            const settingsDoc = await getDoc(doc(db, 'settings', accountId));
            if (settingsDoc.exists()) {
                const settings = settingsDoc.data();
                console.log('✅ サブドメイン: グローバル設定取得完了:', {
                    hasCustomHead: !!settings.customHead,
                    hasCustomBody: !!settings.customBody
                });
                setGlobalSettings(settings);
                return settings;
            } else {
                console.log('ℹ️ サブドメイン: グローバル設定が見つかりません');
                return null;
            }
        } catch (error) {
            // 権限エラーの場合は詳細ログを出力してnullを返す
            if (error.code === 'permission-denied') {
                console.warn('🚫 サブドメイン: グローバル設定へのアクセス権限がありません（未認証ユーザー）');
            } else {
                console.warn('⚠️ サブドメイン: グローバル設定の取得に失敗:', error);
            }
            return null;
        }
    };

    useEffect(() => {
        const handleRoute = async () => {
            try {
                setLoading(true);
                setError('');

                // 現在のURLを解析
                const { subdomain, path } = parseSubdomainURL();

                if (!subdomain) {
                    setError('サブドメインが見つかりません');
                    return;
                }

                // ルーティング情報を取得
                const route = await getSubdomainRoute(subdomain, path);

                if (!route.exists) {
                    setError(route.error || 'ページが見つかりません');
                    return;
                }

                setRouteInfo(route);

                // Firestoreから直接取得したページデータの場合（CORS回避）
                if (route.type === 'firestore-page') {
                    console.log('🎨 Firestoreページデータからレンダリング:', route.pageData.title);
                    if (route.corsWorkaround) {
                        console.log('🔧 CORS問題を回避してFirestoreから直接レンダリング');
                    }

                    const currentPageData = route.pageData;
                    setPageData(currentPageData);

                    // グローバル設定を取得
                    let settings = null;
                    if (currentPageData.accountId) {
                        settings = await fetchGlobalSettings(currentPageData.accountId);
                    }

                    // フォールバック通知
                    let noticeMessage = '';
                    if (route.fallbackToIndex) {
                        noticeMessage = '🏠 インデックスページを表示中';
                    } else if (route.fromRootPageSetting) {
                        noticeMessage = '🏠 ルートページ設定から表示';
                    } else if (route.corsWorkaround) {
                        noticeMessage = '🔧 CORS最適化モード';
                    }

                    // headとbodyを分離して構築
                    // headコンテンツ: ページ固有 + グローバル設定
                    const customHeadContent = [
                        currentPageData.head || '',
                        settings && settings.customHead ? `<!-- Global Settings Custom Head -->\n${settings.customHead}` : ''
                    ].filter(Boolean).join('\n');

                    // ページコンテンツを決定: 完全なHTMLがあればそれを、なければbodyを使用
                    const hasCompleteHTML = (currentPageData.html || currentPageData.content) &&
                        (currentPageData.html || currentPageData.content).includes('<!DOCTYPE') &&
                        (currentPageData.html || currentPageData.content).includes('<html') &&
                        (currentPageData.html || currentPageData.content).includes('</html>');

                    let bodyContent;

                    if (hasCompleteHTML) {
                        // 完全なHTMLファイルの場合、そのまま使用
                        console.log('📄 完全なHTMLファイルを検出、そのまま表示');
                        bodyContent = currentPageData.html || currentPageData.content;
                    } else {
                        // bodyコンテンツのみの場合、CSSと通知を追加
                        bodyContent = `
    ${currentPageData.body || ''}
    ${noticeMessage ? `<div class="status-notice ${route.fallbackToIndex ? 'fallback-notice' : ''} ${route.fromRootPageSetting ? 'root-page-notice' : ''}">${noticeMessage}</div>` : ''}
    <style>
        /* CORS回避用のベーススタイル */
        body {
            margin: 0;
            padding: 0;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', sans-serif;
            line-height: 1.6;
            color: #333;
        }
        .status-notice {
            position: fixed;
            bottom: 10px;
            right: 10px;
            background: rgba(0,123,255,0.1);
            color: #007bff;
            padding: 5px 10px;
            border-radius: 4px;
            font-size: 12px;
            z-index: 9999;
        }
        .fallback-notice {
            background: rgba(255,193,7,0.1);
            color: #856404;
        }
        .root-page-notice {
            background: rgba(40,167,69,0.1);
            color: #155724;
        }
        ${currentPageData.css || ''}
    </style>
`;
                    }

                    if (hasCompleteHTML) {
                        // 完全なHTMLの場合、グローバル設定の挿入処理
                        if (settings && (settings.customHead || settings.customBody)) {
                            console.log('🔧 完全なHTMLにグローバル設定を挿入');
                            bodyContent = insertGlobalSettingsIntoHTML(bodyContent, settings);
                        }
                        // 完全なHTMLの場合はHelmet処理をスキップ
                        setPageData(null);
                    } else {
                        // 部分HTMLの場合、Helmetでヘッダー処理
                        setPageData({
                            ...currentPageData,
                            customHeadContent: customHeadContent
                        });

                        // カスタムbodyを<body>直下に完全な形で配置
                        // 既存のカスタムbody領域を削除
                        const customBodyRegion = document.getElementById('custom-body-subdomain-region');
                        if (customBodyRegion) {
                            customBodyRegion.remove();
                        }

                        // カスタムbodyが存在する場合、領域コンテナを作成
                        if (settings && settings.customBody && settings.customBody.trim()) {
                            const region = document.createElement('div');
                            region.id = 'custom-body-subdomain-region';
                            region.style.display = 'contents'; // 視覚的に存在しないコンテナ
                            
                            // カスタムbodyの内容をそのまま挿入
                            region.innerHTML = settings.customBody;
                            
                            // <div id="root">の直前に挿入
                            const rootElement = document.getElementById('root');
                            if (rootElement) {
                                document.body.insertBefore(region, rootElement);
                            } else {
                                // rootがない場合は最初に挿入
                                document.body.insertBefore(region, document.body.firstChild);
                            }
                        }
                    }

                    setHtmlContent(bodyContent);
                } else if (route.url) {
                    // HTMLファイルの内容を取得（従来の方法）
                const content = await getStaticHTMLContent(route.url);
                if (content) {
                    setHtmlContent(content);
                } else {
                    setError('HTMLファイルの読み込みに失敗しました');
                    }
                } else {
                    setError('ページデータが見つかりません');
                }

            } catch (error) {
                console.error('サブドメインルーティングエラー:', error);
                setError('ページの読み込みに失敗しました');
            } finally {
                setLoading(false);
            }
        };

        handleRoute();
    }, []);

    if (loading) {
        return <LoadingSpinner />;
    }

    if (error) {
        // CORS関連のエラーかどうかを判定
        const isCorsError = error.includes('CORS') || error.includes('Failed to fetch') ||
            error.includes('Access to fetch') || error.includes('blocked by CORS policy');

        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '100vh',
                fontFamily: 'Arial, sans-serif',
                backgroundColor: '#f8f9fa'
            }}>
                <div style={{
                    textAlign: 'center',
                    maxWidth: '600px',
                    padding: '40px',
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
                }}>
                    <h1 style={{
                        color: isCorsError ? '#ff6b35' : '#dc3545',
                        marginBottom: '20px'
                    }}>
                        {isCorsError ? '⚠️ 一時的な読み込み問題' : '404 - ページが見つかりません'}
                    </h1>

                    {isCorsError ? (
                        <div>
                            <p style={{ fontSize: '16px', marginBottom: '20px', color: '#ff6b35' }}>
                                CORS設定の問題により、一時的に表示できません。
                            </p>
                            <div style={{
                                padding: '20px',
                                backgroundColor: '#fff3cd',
                                border: '1px solid #ffeaa7',
                                borderRadius: '4px',
                                marginBottom: '20px'
                            }}>
                                <p style={{ margin: '0 0 10px 0', fontWeight: 'bold', color: '#856404' }}>
                                    🔧 自動修復中
                                </p>
                                <p style={{ margin: '0', fontSize: '14px', color: '#856404' }}>
                                    システムがCORS問題を検出し、別の方法でページを読み込もうとしています。
                                    数秒後にページが自動更新される場合があります。
                                </p>
                            </div>
                            <button
                                onClick={() => window.location.reload()}
                                style={{
                                    backgroundColor: '#007bff',
                                    color: 'white',
                                    border: 'none',
                                    padding: '10px 20px',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '16px'
                                }}
                            >
                                🔄 ページを再読み込み
                            </button>
                        </div>
                    ) : (
                        <p style={{ fontSize: '16px', marginBottom: '20px' }}>{error}</p>
                    )}

                    {routeInfo?.subdomainInfo && (
                        <div style={{
                            padding: '20px',
                            backgroundColor: '#f8f9fa',
                            borderRadius: '4px',
                            marginBottom: '20px'
                        }}>
                            <p><strong>サブドメイン:</strong> {routeInfo.subdomainInfo.subdomain}.suisui.life</p>
                            <p><strong>所有者:</strong> {routeInfo.subdomainInfo.userEmail}</p>
                            <p style={{ marginTop: '15px', color: '#6c757d' }}>
                                {isCorsError
                                    ? 'システムがページの最適な読み込み方法を選択しています。'
                                    : 'このページを表示するには、管理画面からページを作成・公開してください。'
                                }
                            </p>
                        </div>
                    )}

                    {routeInfo?.debug && (
                        <details style={{ textAlign: 'left', marginTop: '20px' }}>
                            <summary style={{ cursor: 'pointer', color: '#007bff' }}>デバッグ情報</summary>
                            <pre style={{
                                backgroundColor: '#f8f9fa',
                                padding: '10px',
                                borderRadius: '4px',
                                fontSize: '12px',
                                overflow: 'auto'
                            }}>
                                {JSON.stringify(routeInfo.debug, null, 2)}
                            </pre>
                        </details>
                    )}
                </div>
            </div>
        );
    }

    if (htmlContent) {
        return (
            <>
                {pageData && (
                    <Helmet>
                        <title>{pageData.title || 'ページ'}</title>
                        {pageData.description && (
                            <meta name="description" content={pageData.description} />
                        )}
                        {pageData.keywords && (
                            <meta name="keywords" content={pageData.keywords} />
                        )}
                        <meta property="og:title" content={pageData.title || 'ページ'} />
                        {pageData.description && (
                            <meta property="og:description" content={pageData.description} />
                        )}
                        <meta property="og:type" content="website" />
                        <meta property="og:url" content={window.location.href} />
                        <meta name="author" content={routeInfo?.subdomainInfo?.userEmail || ''} />
                        {/* AI-friendly meta tags */}
                        <meta name="robots" content="index, follow" />
                        <meta name="language" content="ja" />
                        <link rel="canonical" href={window.location.href} />
                        {/* カスタムhead要素 */}
                        {pageData.customHeadContent && parseHeadContent(pageData.customHeadContent).map((element) => {
                            const Component = element.type;
                            return <Component key={element.key} {...element.props} />;
                        })}
                    </Helmet>
                )}
            <div
                className="subdomain-static-content"
                dangerouslySetInnerHTML={{ __html: htmlContent }}
            />
            </>
        );
    }

    return (
        <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '100vh',
            fontFamily: 'Arial, sans-serif'
        }}>
            <div style={{ textAlign: 'center' }}>
                <h1>エラー</h1>
                <p>ページの表示に失敗しました</p>
            </div>
        </div>
    );
};

export default SubdomainRouter; 